"use client"

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import mix from '@utils/styles/mix'

import styles from './SiteHeader.module.css'

export const MENU_ID = 'menu-serwisu'
const DESKTOP = '(min-width: 1024px)'
const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'

const MenuContext = createContext(null)

const focusables = (root) => [...root.querySelectorAll(FOCUSABLE)].filter(el => el.getClientRects().length > 0)

// Reszta strony poza szufladą: rodzeństwo każdego przodka panelu aż do <body>.
function outsideOf (panel) {
    const outside = []
    for (let node = panel; node && node !== document.body; node = node.parentElement) {
        const parent = node.parentElement
        if (!parent) break
        for (const sibling of parent.children) {
            if (sibling !== node && !sibling.hasAttribute('data-menu-part') && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) outside.push(sibling)
        }
    }
    return outside
}

// Jedno menu serwisu: panel od 1024 px (niemodalny), szuflada poniżej (modalna, reszta strony inert).
export function MenuProvider ({ children }) {
    const [open, setOpen] = useState(false)
    const openerRef = useRef(null)
    const panelRef = useRef(null)
    const returnFocusRef = useRef(false)

    // fokus wraca na przycisk dopiero po zdjęciu inert (sprzątanie efektu niżej)
    const close = useCallback((returnFocus) => {
        returnFocusRef.current = Boolean(returnFocus)
        setOpen(false)
    }, [])

    const toggle = useCallback((opener) => {
        openerRef.current = opener
        setOpen(o => !o)
    }, [])

    // Most do elementów poza drzewem providera (np. zakładka „Więcej"
    // w wyszukiwarce): zdarzenie przełącza menu, stan jest rozgłaszany z powrotem.
    useEffect(() => {
        const onToggle = (e) => toggle(e.detail?.opener || null)
        window.addEventListener('spl:menu-toggle', onToggle)
        return () => window.removeEventListener('spl:menu-toggle', onToggle)
    }, [toggle])

    useEffect(() => {
        window.dispatchEvent(new CustomEvent('spl:menu-state', { detail: { open } }))
    }, [open])

    useEffect(() => {
        const header = document.getElementById('main-navb')
        const panel = panelRef.current
        if (!open) {
            if (returnFocusRef.current) openerRef.current?.focus()
            returnFocusRef.current = false
            return
        }
        if (!header || !panel) return

        const desktop = window.matchMedia(DESKTOP)
        header.setAttribute('data-menu-open', '')

        const madeInert = []
        if (!desktop.matches) {
            for (const el of outsideOf(panel)) {
                if (el.inert) continue
                el.inert = true
                madeInert.push(el)
            }
            document.documentElement.style.overflow = 'hidden'
            panel.querySelector('[data-menu-close]')?.focus()
        }

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                close(true)
                return
            }
            if (e.key !== 'Tab' || !desktop.matches) return

            const items = focusables(panel)
            const first = items[0]
            const last = items[items.length - 1]
            const active = document.activeElement
            const opener = openerRef.current

            if (active === opener && !e.shiftKey) {
                e.preventDefault()
                first?.focus()
            } else if (active === first && e.shiftKey) {
                e.preventDefault()
                opener?.focus()
            } else if (active === last && !e.shiftKey) {
                e.preventDefault()
                const all = focusables(document).filter(el => !panel.contains(el))
                const next = all[all.indexOf(opener) + 1]
                close(false)
                next?.focus()
            }
        }
        const onClick = (e) => {
            if (e.target.closest('[data-menu-toggle]')) return
            if (e.target.closest('[data-menu-close]')) return close(true)
            if (!panel.contains(e.target) || e.target.closest('a[href]')) close(false)
        }
        const onBreakpoint = () => close(false)

        document.addEventListener('keydown', onKeyDown)
        document.addEventListener('click', onClick)
        desktop.addEventListener('change', onBreakpoint)

        return () => {
            header.removeAttribute('data-menu-open')
            madeInert.forEach(el => { el.inert = false })
            document.documentElement.style.overflow = ''
            document.removeEventListener('keydown', onKeyDown)
            document.removeEventListener('click', onClick)
            desktop.removeEventListener('change', onBreakpoint)
        }
    }, [open, close])

    return (
        <MenuContext.Provider value={{ open, toggle, panelRef }}>
            {children}
        </MenuContext.Provider>
    )
}

export function useMenuOpen () {
    return Boolean(useContext(MenuContext)?.open)
}

export function MenuToggle ({ children, className, label }) {
    const { open, toggle } = useContext(MenuContext)
    return (
        <button
            aria-controls={MENU_ID}
            aria-expanded={open}
            aria-label={label}
            className={className}
            data-menu-toggle=""
            onClick={(e) => toggle(e.currentTarget)}
            type="button"
        >
            {children}
        </button>
    )
}

// Linki menu renderuje serwer (children); zamknięte menu jest ukryte i inert.
export function MenuPanel ({ children }) {
    const { open, panelRef } = useContext(MenuContext)
    return (
        <>
            <div className={styles.menuBackdrop} data-menu-close="" data-menu-part="" />
            <nav
                aria-label="Menu serwisu"
                className={mix([styles.menu, open && styles.menuOpen])}
                data-menu-part=""
                id={MENU_ID}
                inert={open ? undefined : ''}
                ref={panelRef}
            >
                <div className={styles.menuHead}>
                    <p className={styles.menuTitle}>Menu</p>
                    <button aria-label="Zamknij menu" className={styles.iconBtn} data-menu-close="" type="button">
                        <SvgIcon name="close" />
                    </button>
                </div>
                {children}
            </nav>
        </>
    )
}
